// Mechanical scope enforcement for scoped scouts.
//
// Three scout-style pipelines hand a sub-agent the dashboard scope as prose
// (the wiki scout, the anatomy discover scout, the workflow agent) and let it
// run free-form analytics queries. Prose is intent, not enforcement: a scout
// that drops the filter reads org-wide rows and cites work from other
// endpoints as if it belonged to the scope. Enforcement is mechanical and
// lives at two boundaries:
//
//   - the QUERY boundary (soft): withScopeGuard wraps analytics_query and
//     flags any spec that never references the scope's ids, both in the
//     result payload the model reads and through an onWarning callback the
//     host surfaces to the operator. Soft by design: a structural reference
//     check cannot judge semantics, so it warns rather than blocks and
//     legitimate id-lookups stay possible.
//   - the EVIDENCE boundary (hard): a citation only counts when a
//     scope-carrying traces query returns rows for it. evidenceScopeSteps
//     narrows a run's scope to the steps that query can express;
//     probeScopeMembership asks "does this cited thing have any activity
//     inside the scope".
//
// Scope dims the traces query cannot express (hosts, projects, urls,
// commands, ...) cannot be enforced mechanically; evidenceScopeSteps returns
// them in `dropped` so callers disclose the gap instead of silently passing.

import { query } from "./dataClient.js";
import { dimSingularOf, isTraceScopeExpressibleDim } from "./scopeDims.js";

// Re-exported so scope-enforcement consumers (target validation in the
// anatomy scout) get the whole enforcement API from this one module.
export { isTraceScopeExpressibleDim };

function stepIds(step) {
    return (Array.isArray(step?.ids) && step.ids.length ? step.ids : step?.id != null ? [step.id] : [])
        .filter((x) => x != null && x !== "")
        .map(String);
}

// Split a run's scope into the steps a traces query can carry and the dims it
// can't. The steps pass through in dashboard shape ({ dim, id, ids }) so they
// can prefix any getTraces scopePath.
export function evidenceScopeSteps(scopePath) {
    const steps = [];
    const dropped = [];
    for (const step of Array.isArray(scopePath) ? scopePath : []) {
        const ids = stepIds(step);
        if (!ids.length) continue;
        if (isTraceScopeExpressibleDim(step.dim)) steps.push({ dim: step.dim, id: ids[0], ids });
        else dropped.push(step.dim);
    }
    return { steps, dropped };
}

// Dims whose scope id appears LITERALLY in any correctly-scoped query spec
// (as a filter value, an IN list member, or a LIKE fragment). The guard can
// only check these structurally; signal scopes lower to namespace values and
// the legacy prose-only dims have no analytics expression at all.
const REFERENCEABLE_DIMS = new Set([
    "endpoint", "session", "agent", "model", "cluster",
    "tool_call", "skill", "memory", "mcp_server", "identity", "file",
]);

// Structural check: does a query spec reference the scope at all? Returns the
// scope steps whose ids never appear anywhere in the spec.
export function buildScopeGuard(scopePath) {
    const steps = [];
    for (const step of Array.isArray(scopePath) ? scopePath : []) {
        const ids = stepIds(step);
        if (ids.length && REFERENCEABLE_DIMS.has(dimSingularOf(step.dim))) {
            steps.push({ dim: step.dim, ids });
        }
    }
    return {
        active: steps.length > 0,
        missingFrom(spec) {
            if (!steps.length) return [];
            let text;
            try {
                text = JSON.stringify(typeof spec === "string" ? JSON.parse(spec) : spec) || "";
            } catch {
                text = String(spec ?? "");
            }
            return steps.filter((s) => !s.ids.some((id) => text.includes(id)));
        },
    };
}

export function scopeWarningText(missing) {
    const what = missing
        .map((s) => `${s.dim} ${s.ids.length > 1 ? `(one of ${s.ids.length} ids)` : s.ids[0]}`)
        .join(", ");
    return `This query never references the run's scope (${what}), so its rows may include `
        + "out-of-scope data. Do not cite or count them as this scope's activity; "
        + "re-run the query with the scope filter applied.";
}

// The analytics result payload is JSON text the model reads AND downstream
// parsers (result previews, python piping) re-parse, so the warning is
// injected as a leading `_scope_warning` field inside the JSON rather than as
// prose around it. Non-JSON payloads get a separate leading text block.
function injectWarning(result, warning) {
    const content = Array.isArray(result?.content) ? result.content : [];
    const first = content[0];
    if (first?.type === "text" && typeof first.text === "string") {
        try {
            const payload = JSON.parse(first.text);
            if (payload && typeof payload === "object" && !Array.isArray(payload)) {
                const text = JSON.stringify({ _scope_warning: warning, ...payload });
                return { ...result, content: [{ ...first, text }, ...content.slice(1)] };
            }
        } catch { /* not JSON; fall through to the text block */ }
    }
    return { ...result, content: [{ type: "text", text: warning }, ...content] };
}

// Wrap an analytics_query tool so every call is checked against the run's
// scope. The wrapped tool behaves identically when the spec references the
// scope (or when the scope carries nothing checkable); otherwise the result
// is flagged and onWarning fires with the missing steps.
export function withScopeGuard(tool, { scopePath, onWarning } = {}) {
    const guard = buildScopeGuard(scopePath);
    if (!guard.active) return tool;
    return {
        ...tool,
        async execute(toolCallId, args) {
            const result = await tool.execute(toolCallId, args);
            const missing = guard.missingFrom(args?.spec);
            if (!missing.length) return result;
            try { onWarning?.(missing); } catch { /* disclosure must not break the call */ }
            return injectWarning(result, scopeWarningText(missing));
        },
    };
}

// Hard membership probe: does `dim:id` have at least one prompt INSIDE the
// run's scope? `scopeSteps` must come from evidenceScopeSteps (inexpressible
// dims already dropped); getTraces fails closed on anything else. Sensitive
// columns are dropped: a probe reads row existence, never content.
export async function probeScopeMembership({ scopeSteps, dim, id, range = null }) {
    const res = await query("getTraces", {
        scopePath: [...scopeSteps, { dim, id }],
        range,
        limit: 1,
        sort: "newest",
        offset: 0,
        canReadSensitivePrompts: false,
    }, { cache: true });
    const rows = Array.isArray(res) ? res : res?.rows || [];
    return rows.length > 0;
}
